// 中央捨牌池視覺
// 對應 PRD §4.4 中央牌池（捨牌區）
//
// === 使用圖片 ===
// - 捨牌：透過 CTileSprite (face=TileFace.Discard) 渲染
//   （TileFace.Discard 內部使用 tile-front-down.png + tiles-source.png；見 tile-sprite.ts）。
// - public/assets/ui/discard-claimed.png（key: 'discard-claimed'，tile_mask 63×99 牌形遮罩）— 被吃/碰/槓的捨牌覆蓋圖
//
// === 排版規則 ===
// 收一條「依時間排序」的捨牌序列（最新一張在尾端），依序排版：
//   - 從左至右排，每 18 張換下一行
//   - 整塊牌池置中於 (centerX, centerY)，行數隨總張數動態增長
//   - 牌縮放固定 TILE_SCALE；step 用 tile-front-down 寬度 × scale 加 gap 計算
// 時間順序由 caller 維護（CGameScene 只在 discard.committed 時把牌加入序列），
// 確保「最新一張一定落在海底最後一格」。

import type { TTile } from '@core/tile';
import type Phaser from 'phaser';

import { SCREEN_CENTER_X, SCREEN_CENTER_Y } from '@config/game-config';

import { CTileSprite, TileFace } from './tile-sprite';

const TILES_PER_ROW = 18; // 每行最大張數（PRD：18/行）
const TILE_STEP_X = 61; // 牌之間間距（含牌身寬，未經 scale）
const TILE_STEP_Y = 96; // 行與行之間間距（未經 scale）；> 牌身高 99 → 各行不互相蓋住
const TILE_SCALE = 0.7; // 捨牌縮放（step 用 TILE_STEP_X × scale 計算，會隨之等比放大）
// 海底頂列 Y 用「固定基準行數」算，不隨實際行數變動：
// 每張牌的 Y 只由 row index 決定，新增行往下長、舊牌 Y 不動（避免整塊上下跳動）。
const DISCARD_BASELINE_ROWS = 3; // 以 3 row 置中為基準，固定頂列 Y
const FADE_IN_MS = 250; // 新捨牌「fade in」時長（玩家出牌動畫尾端用）
// ---- 被吃/碰/槓覆蓋圖（discard-claimed）----
const CLAIMED_COVER_KEY = 'discard-claimed'; // 覆蓋圖 asset key
const CLAIMED_COVER_W = 61; // 覆蓋圖顯示寬（= 捨牌牌身寬，未經 scale；蓋滿整張）
const CLAIMED_COVER_H = 96; // 覆蓋圖顯示高（= 捨牌牌身高，未經 scale；蓋滿整張）
const CLAIMED_COVER_ALPHA = 0.5; // 覆蓋圖透明度（asset 本身為半透明陰影，故用滿值）

// 海底牌池中心：相對畫面正中央的 Y 偏移（負=往上、正=往下）；避開下方選牌預覽與動作按鈕
const DISCARD_CENTER_Y_OFFSET = -80;
const DISCARD_CENTER_X = SCREEN_CENTER_X;
const DISCARD_CENTER_Y = SCREEN_CENTER_Y + DISCARD_CENTER_Y_OFFSET;

export class CDiscardPool {
  private readonly sprites: CTileSprite[] = [];
  private readonly covers: Phaser.GameObjects.Image[] = []; // 被吃/碰/槓的覆蓋圖
  private readonly centerX: number;
  private readonly centerY: number;

  /**
   * 中心座標由內部常數決定（DISCARD_CENTER_X / Y）；
   * 若需要客製，可選擇性傳入 overrides — 預設用畫面中央上方一點。
   */
  constructor(
    private readonly scene: Phaser.Scene,
    centerX: number = DISCARD_CENTER_X,
    centerY: number = DISCARD_CENTER_Y
  ) {
    this.centerX = centerX;
    this.centerY = centerY;
  }

  /**
   * 用一條「依時間排序」的捨牌序列重繪整個 pool（最新一張在尾端）。
   * 簡化做法：清掉舊 sprite 重建（MVP；之後可改 diff-based 加 animation）。
   * @param opts.fadeInLast 最新一張（序列尾端）以 alpha 0→1 淡入（玩家單擊出牌動畫尾端用）。
   */
  update(
    tiles: readonly TTile[],
    opts?: {
      readonly fadeInLast?: boolean;
      readonly claimedIds?: ReadonlySet<number>;
      readonly onFadeInComplete?: () => void;
    }
  ): void {
    this.clear();
    this.renderLinear(tiles, opts?.claimedIds);
    if (opts?.fadeInLast) {
      if (this.sprites.length === 0) {
        opts.onFadeInComplete?.();
        return;
      }
      const last = this.sprites[this.sprites.length - 1]!;
      last.setAlpha(0);
      this.scene.tweens.add({
        targets: last,
        alpha: 1,
        duration: FADE_IN_MS,
        ease: 'Linear',
        ...(opts.onFadeInComplete ? { onComplete: opts.onFadeInComplete } : {}),
      });
    }
  }

  clear(): void {
    for (const s of this.sprites) s.destroy();
    this.sprites.length = 0;
    for (const c of this.covers) c.destroy();
    this.covers.length = 0;
  }

  /**
   * 把 flatten 後的捨牌依「從左至右、18 張換一行」排版，整塊置中於 (centerX, centerY)。
   */
  private renderLinear(tiles: readonly TTile[], claimedIds?: ReadonlySet<number>): void {
    if (tiles.length === 0) return;

    const stepX = TILE_STEP_X * TILE_SCALE;
    const stepY = TILE_STEP_Y * TILE_SCALE;

    // 一整行的寬度 = (張數 - 1) × stepX；以 tiles.length 計，最後一行可能不足
    // 為了視覺整齊，整塊以「滿行寬度」對中
    const fullRowWidth = (TILES_PER_ROW - 1) * stepX;
    const startX = this.centerX - fullRowWidth / 2;
    // 頂列 Y 固定：以 DISCARD_BASELINE_ROWS 為基準置中，不隨實際行數變動
    // → 每張牌 Y 只由 row index 決定，海底長高時舊牌不會往上跳
    const baselineBlockHeight = (DISCARD_BASELINE_ROWS - 1) * stepY;
    const startY = this.centerY - baselineBlockHeight / 2;

    for (let i = 0; i < tiles.length; i++) {
      const tile = tiles[i];
      if (!tile) continue;
      const row = Math.floor(i / TILES_PER_ROW);
      const col = i % TILES_PER_ROW;
      const x = startX + col * stepX;
      const y = startY + row * stepY;
      const sprite = new CTileSprite(this.scene, x, y, tile, TileFace.Discard);
      sprite.setScale(TILE_SCALE);
      this.sprites.push(sprite);

      // 被吃/碰/槓：在該張捨牌上疊覆蓋圖（牌仍留在海底，視覺上被蓋住代表被吃掉）
      if (claimedIds?.has(tile.id)) {
        const cover = this.scene.add
          .image(x, y, CLAIMED_COVER_KEY)
          .setDisplaySize(CLAIMED_COVER_W * TILE_SCALE, CLAIMED_COVER_H * TILE_SCALE)
          .setAlpha(CLAIMED_COVER_ALPHA);
        this.covers.push(cover);
      }
    }
  }
}
